import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Button, Input, message } from 'antd';
import apiService from '../../services/api';
import { ServiceType } from '../../services/api';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: linear-gradient(
    135deg,
    #181A20 0%,
    #1a1d26 25%,
    #0f1115 50%,
    #1a1d26 75%,
    #181A20 100%
  );
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
      circle at 20% 20%,
      rgba(14, 203, 129, 0.05) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 80% 80%,
      rgba(14, 203, 129, 0.03) 0%,
      transparent 50%
    );
    pointer-events: none;
  }
`;

const Card = styled.div`
  width: 100%;
  max-width: 420px;
  padding: 40px;
  background-color: #1E2329;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  border: 1px solid #2B3139;
  position: relative;
  z-index: 1;
`;

const Logo = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 20px;

  img {
    height: 60px;
    width: auto;
  }
`;

const Title = styled.h1`
  text-align: center;
  font-size: 24px;
  margin-bottom: 8px;
  color: #EAECEF;
`;

const Subtitle = styled.p`
  text-align: center;
  font-size: 14px;
  color: #848E9C;
  margin-bottom: 24px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  color: #EAECEF;
  font-size: 14px;
  font-weight: 500;
`;

const ErrorText = styled.div`
  color: #F6465D;
  font-size: 12px;
  margin-top: 4px;
  min-height: 16px;
`;

const PrimaryButton = styled(Button)`
  width: 100%;
  height: 40px;
  background-color: #0ECB81 !important;
  border: none !important;
  font-weight: 600;
  color: #0B0E11 !important;
  transition: opacity 0.3s ease;

  &:hover {
    background-color: #0ECB81 !important;
    opacity: 0.8 !important;
  }

  &:focus {
    background-color: #0ECB81 !important;
    opacity: 1 !important;
  }
`;

const BackLink = styled.div`
  text-align: center;
  margin-top: 20px;
  font-size: 14px;
  color: #848E9C;

  a {
    color: ${({ theme }) => theme.colors.text.primary};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 24px;
`;

const StepDot = styled.div<{ active: boolean; completed: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ active, completed }) =>
    completed ? '#0ECB81' : active ? '#0ECB81' : '#474D57'};
  opacity: ${({ active, completed }) => (active || completed ? 1 : 0.5)};
  transition: all 0.3s ease;
`;

const StyledInput = styled(Input)`
  background-color: #2B3139 !important;
  border: 1px solid #474D57 !important;
  color: #EAECEF !important;

  &::placeholder {
    color: #848E9C !important;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary} !important;
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary} !important;
    box-shadow: 0 0 0 2px rgba(14, 203, 129, 0.1) !important;
  }

  input {
    background-color: transparent !important;
    color: #EAECEF !important;
  }
`;

const StyledPasswordInput = styled(Input.Password)`
  background-color: #2B3139 !important;
  border: 1px solid #474D57 !important;

  input {
    background-color: transparent !important;
    color: #EAECEF !important;
  }

  &::placeholder {
    color: #848E9C !important;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary} !important;
  }

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.primary} !important;
    box-shadow: 0 0 0 2px rgba(14, 203, 129, 0.1) !important;
  }

  .ant-input {
    background-color: transparent !important;
    color: #EAECEF !important;
  }

  .ant-input-suffix {
    color: #848E9C !important;
  }
`;

const ResendText = styled.div`
  text-align: center;
  margin-top: 12px;
  font-size: 13px;
  color: #848E9C;

  button {
    background: none;
    border: none;
    color: ${({ theme }) => theme.colors.text.primary};
    cursor: pointer;
    padding: 0;
    font-size: 13px;

    &:hover {
      text-decoration: underline;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }
`;

const coreService = apiService.getService(ServiceType.CORE);

type Step = 'email' | 'code' | 'password';

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('email');
  const [loading, setLoading] = useState(false);

  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [emailError, setEmailError] = useState('');
  const [codeError, setCodeError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');

  const validateEmail = (value: string) => {
    if (!value) {
      setEmailError('Email is required');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    setEmailError('');
    return true;
  };

  const validateCode = (value: string) => {
    if (!value) {
      setCodeError('Verification code is required');
      return false;
    }
    if (value.trim().length < 4) {
      setCodeError('Please enter the code from your email');
      return false;
    }
    setCodeError('');
    return true;
  };

  const validatePassword = (value: string) => {
    if (!value) {
      setPasswordError('Password is required');
      return false;
    }
    if (value.length < 6) {
      setPasswordError('Password must be at least 6 characters long');
      return false;
    }
    setPasswordError('');
    return true;
  };

  const validateConfirmPassword = (value: string) => {
    if (!value) {
      setConfirmPasswordError('Please confirm your password');
      return false;
    }
    if (value !== newPassword) {
      setConfirmPasswordError('Passwords do not match');
      return false;
    }
    setConfirmPasswordError('');
    return true;
  };

  const handleSendCode = async () => {
    if (!validateEmail(email)) return;
    setLoading(true);
    try {
      await coreService.post('/auth/forgot-password', { email });
      message.success('Verification code sent. Please check your email.');
      setStep('code');
    } catch (error: any) {
      console.error('Forgot password error:', error);
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('Failed to send verification code. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!validateCode(code)) return;
    setLoading(true);
    try {
      await coreService.post('/auth/verify-code', { email, code: code.trim() });
      message.success('Code verified.');
      setStep('password');
    } catch (error: any) {
      console.error('Verify code error:', error);
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('Invalid or expired code. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    const isPwdValid = validatePassword(newPassword);
    const isConfirmValid = validateConfirmPassword(confirmPassword);
    if (!isPwdValid || !isConfirmValid) return;

    setLoading(true);
    try {
      await coreService.post('/auth/reset-password', {
        email,
        code: code.trim(),
        newPassword,
      });
      message.success('Password reset successful! Please sign in.');
      navigate('/signin');
    } catch (error: any) {
      console.error('Reset password error:', error);
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('Failed to reset password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setLoading(true);
    try {
      await coreService.post('/auth/forgot-password', { email });
      message.success('Verification code resent.');
    } catch (error: any) {
      console.error('Resend code error:', error);
      message.error('Failed to resend code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    if (step === 'email') {
      return (
        <>
          <Subtitle>Enter the email associated with your account and we'll send a verification code.</Subtitle>
          <div style={{ marginBottom: '8px' }}>
            <Label>Email</Label>
            <StyledInput
              placeholder="Email address"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) validateEmail(e.target.value);
              }}
              onBlur={(e) => validateEmail(e.target.value)}
              onPressEnter={handleSendCode}
              size="large"
              status={emailError ? 'error' : ''}
              autoFocus
            />
            <ErrorText>{emailError}</ErrorText>
          </div>
          <PrimaryButton onClick={handleSendCode} loading={loading}>
            SEND CODE
          </PrimaryButton>
        </>
      );
    }

    if (step === 'code') {
      return (
        <>
          <Subtitle>We sent a verification code to <strong style={{ color: '#EAECEF' }}>{email}</strong>. Enter it below.</Subtitle>
          <div style={{ marginBottom: '8px' }}>
            <Label>Verification Code</Label>
            <StyledInput
              placeholder="Enter code"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                if (codeError) validateCode(e.target.value);
              }}
              onBlur={(e) => validateCode(e.target.value)}
              onPressEnter={handleVerifyCode}
              size="large"
              status={codeError ? 'error' : ''}
              autoFocus
            />
            <ErrorText>{codeError}</ErrorText>
          </div>
          <PrimaryButton onClick={handleVerifyCode} loading={loading}>
            VERIFY CODE
          </PrimaryButton>
          <ResendText>
            Didn't receive a code?{' '}
            <button onClick={handleResend} disabled={loading} type="button">
              Resend
            </button>
          </ResendText>
        </>
      );
    }

    return (
      <>
        <Subtitle>Choose a new password for your account.</Subtitle>
        <div style={{ marginBottom: '8px' }}>
          <Label>New Password</Label>
          <StyledPasswordInput
            placeholder="New password"
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              if (passwordError) validatePassword(e.target.value);
              if (confirmPassword && confirmPasswordError) validateConfirmPassword(confirmPassword);
            }}
            onBlur={(e) => validatePassword(e.target.value)}
            onPressEnter={handleResetPassword}
            size="large"
            status={passwordError ? 'error' : ''}
            autoFocus
          />
          <ErrorText>{passwordError}</ErrorText>
        </div>
        <div style={{ marginBottom: '8px' }}>
          <Label>Confirm Password</Label>
          <StyledPasswordInput
            placeholder="Confirm password"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              if (confirmPasswordError) validateConfirmPassword(e.target.value);
            }}
            onBlur={(e) => validateConfirmPassword(e.target.value)}
            onPressEnter={handleResetPassword}
            size="large"
            status={confirmPasswordError ? 'error' : ''}
          />
          <ErrorText>{confirmPasswordError}</ErrorText>
        </div>
        <PrimaryButton onClick={handleResetPassword} loading={loading}>
          RESET PASSWORD
        </PrimaryButton>
      </>
    );
  };

  return (
    <Container>
      <Card>
        <Logo>
          <img src="/Algotrade - White.png" alt="Algotrade" />
        </Logo>
        <Title>Forgot Password</Title>
        <StepIndicator>
          <StepDot active={step === 'email'} completed={step !== 'email'} />
          <StepDot active={step === 'code'} completed={step === 'password'} />
          <StepDot active={step === 'password'} completed={false} />
        </StepIndicator>
        {renderStep()}
        <BackLink>
          Remembered your password? <Link to="/signin">Sign In</Link>
        </BackLink>
      </Card>
    </Container>
  );
};

export default ForgotPasswordPage;
